import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import React from 'react';
import { parseDateString } from '../../bidAnalysisTemplate.logic';
import classes from '../../bidAnalysisTemplate.module.scss';
import { FormField } from '../shared/FormField';
import { MarketDataTooltip } from '../shared/MarketDataTooltip';
import { SubSectionHeader } from '../shared/SubSectionHeader';

// Helper function to format date for display (uses parseDateString so server dates show as-is, no timezone shift)
const formatDateForDisplay = (date: any): string => {
  if (!date) return 'N/A';
  try {
    const dateObj = parseDateString(date);
    if (!dateObj || isNaN(dateObj.getTime())) return 'N/A';
    // Format as MM/DD/YYYY
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${month}/${day}/${year}`;
  } catch {
    return 'N/A';
  }
};

interface CurrentMarketStateSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  formData: any;
  templateData?: any;
  validationErrors: Record<string, string>;
  onFieldChange: (field: string, value: any) => void;
  onFieldBlur: (field: string, value: any) => void;
  onFieldKeyDown?: (field: string, e: React.KeyboardEvent) => void;
  ndcValidated?: boolean;
  ndcOptions?: Array<any>;
  templateType?: string; // 'mas' | 'nc' - to distinguish between MAS and NC templates
  vaDataAsOf?: string | null;
  dodDataAsOf?: string | null;
}

export const CurrentMarketStateSection: React.FC<CurrentMarketStateSectionProps> = ({
  isExpanded,
  onToggle,
  formData,
  templateData: _templateData,
  validationErrors,
  onFieldChange,
  onFieldBlur,
  onFieldKeyDown,
  ndcValidated: _ndcValidated = false,
  ndcOptions: _ndcOptions = [],
  templateType,
  vaDataAsOf,
  dodDataAsOf,
}) => {
  // Usage Period dates are always editable (no locking after fetch)
  const shouldShowEditableDates = true;
  const isNC = templateType === 'nc';
  return (
    <div className={classes.subSection}>
      <SubSectionHeader
        title="Current Market State"
        isExpanded={isExpanded}
        onToggle={onToggle}
        titleSuffix={<MarketDataTooltip vaDataAsOf={vaDataAsOf} dodDataAsOf={dodDataAsOf} />}
      />
      {isExpanded && (
        <div className={classes.subSectionContent}>
          <div className={classes.usagePeriod}>
            <h4>Usage Period:</h4>
            <div className={classes.formGrid2}>
              <FormField label="Start Date">
                {shouldShowEditableDates || !formData.usagePeriodStartDate ? (
                  <Calendar
                    value={parseDateString(formData.usagePeriodStartDate)}
                    onChange={e => {
                      if (e.value) {
                        // Store as Date object - handleFieldBlur will handle formatting
                        onFieldChange('usagePeriodStartDate', e.value);
                        onFieldBlur('usagePeriodStartDate', e.value);
                      } else {
                        // Clear the date
                        onFieldChange('usagePeriodStartDate', null);
                        onFieldBlur('usagePeriodStartDate', null);
                      }
                    }}
                    dateFormat="mm/dd/yy"
                    showIcon
                    placeholder="Select Start Date"
                  />
                ) : (
                  <div className={classes.textDisplay}>
                    {formatDateForDisplay(formData.usagePeriodStartDate)}
                  </div>
                )}
              </FormField>
              <FormField label="End Date" error={validationErrors.usagePeriodEndDate}>
                {shouldShowEditableDates || !formData.usagePeriodEndDate ? (
                  <Calendar
                    value={parseDateString(formData.usagePeriodEndDate)}
                    onChange={e => {
                      if (e.value) {
                        // Validate that end date is not before start date
                        const startDate = parseDateString(formData.usagePeriodStartDate);

                        if (startDate && e.value.getTime() < startDate.getTime()) {
                          // Don't update the date if it's before start date
                          // The validation error will be set by the parent component
                          return;
                        }

                        // Store as Date object - handleFieldBlur will handle formatting
                        onFieldChange('usagePeriodEndDate', e.value);
                        onFieldBlur('usagePeriodEndDate', e.value);
                      } else {
                        // Clear the date
                        onFieldChange('usagePeriodEndDate', null);
                        onFieldBlur('usagePeriodEndDate', null);
                      }
                    }}
                    dateFormat="mm/dd/yy"
                    showIcon
                    placeholder="Select End Date"
                    minDate={parseDateString(formData.usagePeriodStartDate) ?? undefined}
                    className={validationErrors.usagePeriodEndDate ? 'p-invalid' : ''}
                  />
                ) : (
                  <div className={classes.textDisplay}>
                    {formatDateForDisplay(formData.usagePeriodEndDate)}
                  </div>
                )}
              </FormField>
            </div>
          </div>

          {isNC ? (
            // NC template: Show NC-specific usage fields
            <div className={classes.formGrid2}>
              <FormField label="VA Annual NC Est" error={validationErrors.vaAnnualNcEst}>
                <InputNumber
                  value={formData.vaAnnualNcEst ? parseFloat(formData.vaAnnualNcEst) : null}
                  onValueChange={e => onFieldChange('vaAnnualNcEst', e.value)}
                  onBlur={e => onFieldBlur('vaAnnualNcEst', e)}
                  onKeyDown={e => onFieldKeyDown?.('vaAnnualNcEst', e)}
                  placeholder="Enter VA Annual NC Est"
                  className={validationErrors.vaAnnualNcEst ? 'p-invalid' : ''}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  min={0}
                />
              </FormField>
              <FormField label="DOD Annual NC Est" error={validationErrors.dodAnnualNcEst}>
                <InputNumber
                  value={formData.dodAnnualNcEst ? parseFloat(formData.dodAnnualNcEst) : null}
                  onValueChange={e => onFieldChange('dodAnnualNcEst', e.value)}
                  onBlur={e => onFieldBlur('dodAnnualNcEst', e)}
                  onKeyDown={e => onFieldKeyDown?.('dodAnnualNcEst', e)}
                  placeholder="Enter DOD Annual NC Est"
                  className={validationErrors.dodAnnualNcEst ? 'p-invalid' : ''}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  min={0}
                />
              </FormField>
              <FormField label="IHS Annual NC Est" error={validationErrors.ihsAnnualNcEst}>
                <InputNumber
                  value={formData.ihsAnnualNcEst ? parseFloat(formData.ihsAnnualNcEst) : null}
                  onValueChange={e => onFieldChange('ihsAnnualNcEst', e.value)}
                  onBlur={e => onFieldBlur('ihsAnnualNcEst', e)}
                  onKeyDown={e => onFieldKeyDown?.('ihsAnnualNcEst', e)}
                  placeholder="Enter IHS Annual NC Est"
                  className={validationErrors.ihsAnnualNcEst ? 'p-invalid' : ''}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  min={0}
                />
              </FormField>
              <FormField label="BOP Annual NC Est" error={validationErrors.bopAnnualNcEst}>
                <InputNumber
                  value={formData.bopAnnualNcEst ? parseFloat(formData.bopAnnualNcEst) : null}
                  onValueChange={e => onFieldChange('bopAnnualNcEst', e.value)}
                  onBlur={e => onFieldBlur('bopAnnualNcEst', e)}
                  onKeyDown={e => onFieldKeyDown?.('bopAnnualNcEst', e)}
                  placeholder="Enter BOP Annual NC Est"
                  className={validationErrors.bopAnnualNcEst ? 'p-invalid' : ''}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  min={0}
                />
              </FormField>
              <FormField label="NC Est Annual Usage" error={validationErrors.ncEstAnnualUsage}>
                <InputNumber
                  value={formData.ncEstAnnualUsage ? parseFloat(formData.ncEstAnnualUsage) : null}
                  readOnly
                  disabled
                  placeholder="NC Est Annual Usage"
                  className={validationErrors.ncEstAnnualUsage ? 'p-invalid' : ''}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  min={0}
                />
              </FormField>
            </div>
          ) : (
            // MAS template: Show MAS usage fields
            <div className={classes.formGrid2}>
              <FormField label="VA Annual Usage" error={validationErrors.vaAnnualUsage}>
                <InputNumber
                  value={formData.vaAnnualUsage ? parseFloat(formData.vaAnnualUsage) : null}
                  onValueChange={e => onFieldChange('vaAnnualUsage', e.value)}
                  onBlur={e => onFieldBlur('vaAnnualUsage', e)}
                  onKeyDown={e => onFieldKeyDown?.('vaAnnualUsage', e)}
                  placeholder="Enter VA Annual Usage"
                  className={validationErrors.vaAnnualUsage ? 'p-invalid' : ''}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  min={0}
                />
              </FormField>
              <FormField label="DOD Annual Usage" error={validationErrors.dodAnnualUsage}>
                <InputNumber
                  value={formData.dodAnnualUsage ? parseFloat(formData.dodAnnualUsage) : null}
                  onValueChange={e => onFieldChange('dodAnnualUsage', e.value)}
                  onBlur={e => onFieldBlur('dodAnnualUsage', e)}
                  onKeyDown={e => onFieldKeyDown?.('dodAnnualUsage', e)}
                  placeholder="Enter DOD Annual Usage"
                  className={validationErrors.dodAnnualUsage ? 'p-invalid' : ''}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  min={0}
                />
              </FormField>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
